import { useEffect, useState, useRef } from 'react';
import { ReactComponent as PlaySvg } from '../../../assets/svg/play.svg';
import { ReactComponent as ThumbsUpLineSvg } from '../../../assets/svg/thumbs-up-line.svg';
import { ReactComponent as ThumbsUpSvg } from '../../../assets/svg/thumbs-up.svg';
import { ReactComponent as CrownSvg } from '../../../assets/svg/crown.svg';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import About from './about';
import Result from './result';
import Routine from './routine';
import Instruction from './instruction';
import request from '../../../lib/request';
import InstructionPopup from './instructionPopup';

const ChallengePopup = ({
  dialogRef,
  closeDialog,
  openStartPopup,
  goToDashboardChallenge,
  selectedChallengeId,
  challengePopupOpen,
}) => {
  const instructionRef = useRef(null);

  const [rated, setRated] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [instructionOpen, setInstructionOpen] = useState(false);

  useEffect(() => {
    if (challengePopupOpen) {
      getChallenge();
    }
  }, [challengePopupOpen]);

  const getChallenge = async () => {
    try {
      const { result } = await request.get(`/challenge/${selectedChallengeId}`);
      if (result) {
        setChallenge(result);
        setRated(result.rated);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const likeChallenge = async () => {
    try {
      const { result } = await request.put(
        `/user/challenge/${selectedChallengeId}/like`,
        { like: !rated }
      );
      if (result) {
        setRated(result.like);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const openInstruction = () => {
    if (instructionRef.current) {
      instructionRef.current.showModal();
      setInstructionOpen(true);
    }
  };

  const closeInstruction = () => {
    if (instructionRef.current) {
      instructionRef.current.close();
      setInstructionOpen(false);
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className='w-full h-full max-w-lg max-h-full bg-transparent'
    >
      {challenge && (
        <div className='bg-secondary fixed bottom-0 w-full max-w-lg rounded-t-3xl p-4 h-[95%] flex flex-col text-white overflow-y-hidden border-t border-tertiary'>
          <div className='overflow-y-auto scrollbar-hidden rounded-xl pb-8'>
            <div className='relative'>
              <img
                src={challenge.bannerUrl}
                alt='challenge poster'
                className='w-full rounded-xl'
              />
              <button
                className='text-2xl absolute top-2 right-2 size-6 bg-tertiary/70 rounded-full flex items-center justify-center '
                onClick={closeDialog}
              >
                🅧
              </button>
              {challenge.premium && (
                <div className='absolute top-0 left-0 bg-accent px-3 py-1 rounded-tl-xl rounded-br-xl'>
                  <CrownSvg className='size-5' />
                </div>
              )}
            </div>
            <h1 className='text-center mt-4 text-lg font-bold'>
              {challenge.title}
            </h1>
            {challenge.category !== 'UPCOMING' && (
              <div className='flex justify-center mt-6'>
                <button
                  className='bg-white text-black rounded-md py-1 w-full text-xl flex justify-center items-center gap-1 '
                  onClick={
                    challenge.enrolled
                      ? goToDashboardChallenge
                      : () => openStartPopup(challenge)
                  }
                >
                  <PlaySvg className='size-6' />
                  <span
                    className='text-xl'
                    style={{ fontFamily: "'Teko', sans-serif" }}
                  >
                    Start
                  </span>
                </button>
              </div>
            )}
            <p className='mt-4 mb-6 whitespace-pre-line text-sm'>
              {challenge.description}
            </p>
            <div className='mb-6'>
              <div
                className='flex justify-start items-end gap-10 mt-4 px-4 text-sm'
                style={{ fontFamily: "'Teko', sans-serif" }}
              >
                <button
                  className='flex flex-col justify-center items-center'
                  onClick={likeChallenge}
                >
                  {rated ? (
                    <ThumbsUpSvg
                      className='size-5'
                      fill='white'
                    />
                  ) : (
                    <ThumbsUpLineSvg
                      className='size-5'
                      stroke='white'
                    />
                  )}
                  <span className='text-lg'>Rated</span>
                </button>
                <button className='flex flex-col justify-center items-center '>
                  <span>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                      strokeWidth='1.5'
                      stroke='currentColor'
                      className='size-6'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M9 8.25H7.5a2.25 2.25 0 0 0-2.25 2.25v9a2.25 2.25 0 0 0 2.25 2.25h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25H15m0-3-3-3m0 0-3 3m3-3V15'
                      />
                    </svg>
                  </span>
                  <span className='text-lg'>Share</span>
                </button>
                {challenge.instructions?.length > 0 && (
                  <button
                    className='flex flex-col justify-center items-center'
                    onClick={openInstruction}
                  >
                    <InformationCircleIcon className='size-6' />
                    <span className='text-lg'>Instruction</span>
                  </button>
                )}
              </div>
            </div>
            {challengePopupOpen && (
              <div className='space-y-4'>
                <About challenge={challenge} />
                <Instruction
                  challenge={challenge}
                  openInstruction={openInstruction}
                />
                <Result challenge={challenge} />
                <Routine challenge={challenge} />
              </div>
            )}
          </div>
        </div>
      )}
      <InstructionPopup
        dialogRef={instructionRef}
        closeDialog={closeInstruction}
        challenge={challenge}
        instructionOpen={instructionOpen}
      />
    </dialog>
  );
};

export default ChallengePopup;
